import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { UPLOAD_RETRY_MS } from '@constants/timing';
import { ApiError, ERROR_CODE, errorText, type UploadOpts } from '@lib/api';
import { ImageUnreadable, scaleImage, type ScaleOpts } from '@lib/utils/image';

/** Where one file stands; only the states that have them carry progress, an upload id or an error. */
export type UploadState =
  | { status: 'preparing' | 'waiting' | 'retrying' }
  | { status: 'uploading'; progress: number }
  | { status: 'done'; uploadId: string }
  | { status: 'failed'; error: string; retryable: boolean };
export type UploadSlotStatus = UploadState['status'];

/** One file in a slot (a document kind, a checklist item's photo): what it is, its preview, and its state. */
export type UploadSlot<K extends string> = {
  kind: K;
  name: string;
  size: number;
  type: string;
  /** an object URL of the picked file, for "Lihat" and the thumbnail; released when replaced, removed or left */
  previewUrl: string;
} & UploadState;

export interface UploadQueueOpts<K extends string> {
  send: (kind: K, body: Blob, opts: UploadOpts) => Promise<{ uploadId: string }>;
  /** types accepted as they are (`application/pdf`, `image/jpeg`, ...) */
  types: readonly string[];
  /** what a kind takes: PDF and/or pictures, and the largest body sent (a picture after it is scaled down) */
  rule: (kind: K) => { formats: readonly ('pdf' | 'image')[]; maxBytes: number };
  /** the largest picture picked (it is scaled down before it is sent, so it may start larger than `maxBytes`) */
  maxPickBytes: number;
  scale: Omit<ScaleOpts, 'accepted'>;
  concurrency: number;
  /** passing failures retried automatically this many times (then a manual retry) */
  autoRetries: number;
  /** `pdfActive`: a PDF refused for active content (scripts, embedded files); the server's sentence otherwise */
  messages: { badType: string; photoOnly: string; pdfOnly: string; tooLarge: (max: string) => string; unreadable: string; pdfActive?: string };
  formatBytes: (n: number) => string;
}
export type UploadMessages = UploadQueueOpts<string>['messages'];

/** Failures worth another try: no connection (or a stalled one), a server error, too many requests. */
const transient = (e: unknown) => e instanceof ApiError && (e.status === 0 || e.status >= 500 || e.status === 429);
const isImage = (t: string) => t.startsWith('image/');
const EXT_TYPE: Record<string, string> = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' };
/** The file's type, from the browser or (some Android pickers send none) from its extension. */
const typeOf = (f: File) => f.type || EXT_TYPE[f.name.split('.').pop()?.toLowerCase() ?? ''] || '';

/** One run of one slot's file; `gen` is the slot's pick it belongs to (a newer pick makes it stale). */
interface Job { key: string; gen: number; ctl: AbortController }

/**
 * Files picked for upload, one per slot key, kept in memory only. Each is checked (type, size), a photo scaled down,
 * then sent with its progress, `concurrency` at a time. A passing failure (offline, 5xx, 429) is retried by itself:
 * offline waits for the connection to return ("waiting"), else after a growing pause ("retrying"); after `autoRetries`
 * it stays "failed" with a manual retry. A job of a replaced or removed file is aborted and never writes into the slot
 * again (it counts against `concurrency` until it has settled). The original file stays in memory, so `resendAll` can
 * send everything again (an upload expired in the server's 24 h quarantine). Leaving the page stops what is running.
 */
export function useUploadQueue<K extends string>(o: UploadQueueOpts<K>) {
  const [slots, setSlots] = useState<Partial<Record<string, UploadSlot<K>>>>({});
  const files = useRef(new Map<string, { kind: K; file: File; attempts: number; gen: number }>());
  const queue = useRef<string[]>([]);
  const running = useRef(new Set<Job>());
  const timers = useRef(new Map<string, number>());
  const alive = useRef(true);
  const opts = useRef(o);
  const slotsRef = useRef(slots);
  useLayoutEffect(() => {
    opts.current = o;
    slotsRef.current = slots;
  });

  /** The slot's new state; ignored when `job` is no longer the slot's current run (replaced, removed, aborted). */
  const setState = useCallback((key: string, state: UploadState, job?: Job) => {
    if (job && (job.ctl.signal.aborted || files.current.get(key)?.gen !== job.gen)) return;
    setSlots((s) => {
      const old = s[key];
      if (!old) return s;
      const { kind, name, size, type, previewUrl } = old;
      return { ...s, [key]: { kind, name, size, type, previewUrl, ...state } };
    });
  }, []);

  const pumpRef = useRef<() => void>(() => undefined);
  const enqueue = useCallback((key: string) => {
    if (!queue.current.includes(key)) queue.current.push(key);
    pumpRef.current();
  }, []);

  const run = useCallback(
    async (job: Job) => {
      const { key, ctl } = job;
      const signal = ctl.signal;
      const entry = files.current.get(key);
      if (!entry || entry.gen !== job.gen) return;
      const c = opts.current;
      try {
        setState(key, { status: 'preparing' }, job);
        let body: Blob = entry.file;
        if (isImage(typeOf(entry.file))) body = await scaleImage(entry.file, { ...c.scale, accepted: c.types.filter(isImage) });
        if (signal.aborted) return;
        if (body.size > c.rule(entry.kind).maxBytes) throw new RangeError();
        if (!navigator.onLine) throw new ApiError('', 0, ERROR_CODE.NETWORK);
        setState(key, { status: 'uploading', progress: 0 }, job);
        const res = await c.send(entry.kind, body, { signal, onProgress: (sent, total) => setState(key, { status: 'uploading', progress: total ? sent / total : 0 }, job) });
        // replaced or removed while the answer was on its way: this upload id belongs to no slot
        if (signal.aborted) return;
        entry.attempts = 0;
        setState(key, { status: 'done', uploadId: res.uploadId }, job);
      } catch (e) {
        if (signal.aborted || (e instanceof ApiError && e.code === ERROR_CODE.ABORTED) || !alive.current) return;
        if (e instanceof ImageUnreadable) return setState(key, { status: 'failed', error: c.messages.unreadable, retryable: false }, job);
        if (e instanceof RangeError) return setState(key, { status: 'failed', error: c.messages.tooLarge(c.formatBytes(c.rule(entry.kind).maxBytes)), retryable: false }, job);
        if (!transient(e)) {
          const active = e instanceof ApiError && e.code === ERROR_CODE.HUB_FILE_INVALID && typeOf(entry.file) === 'application/pdf' && /skrip|script|lampiran/i.test(e.message);
          return setState(key, { status: 'failed', error: active && c.messages.pdfActive ? c.messages.pdfActive : errorText(e), retryable: false }, job);
        }
        // offline: wait for the connection (the 'online' listener requeues); else retry after a pause, a few times
        if (!navigator.onLine) return setState(key, { status: 'waiting' }, job);
        if (entry.attempts < c.autoRetries) {
          // a 429's Retry-After wins over our own pause when it is longer
          const asked = e instanceof ApiError && e.retryAfterS ? e.retryAfterS * 1000 : 0;
          const wait = Math.max(asked, UPLOAD_RETRY_MS[Math.min(entry.attempts, UPLOAD_RETRY_MS.length - 1)]);
          entry.attempts++;
          setState(key, { status: 'retrying' }, job);
          const t = window.setTimeout(() => {
            timers.current.delete(key);
            enqueue(key);
          }, wait);
          timers.current.set(key, t);
          return;
        }
        setState(key, { status: 'failed', error: errorText(e), retryable: true }, job);
      }
    },
    [setState, enqueue],
  );

  useEffect(() => {
    pumpRef.current = () => {
      while (alive.current && running.current.size < opts.current.concurrency && queue.current.length) {
        const key = queue.current.shift()!;
        const entry = files.current.get(key);
        if (!entry) continue;
        const job: Job = { key, gen: entry.gen, ctl: new AbortController() };
        running.current.add(job);
        void run(job).finally(() => {
          running.current.delete(job);
          pumpRef.current();
        });
      }
    };
    pumpRef.current();
  }, [run]);

  /** Stop the slot's work: its job is aborted (it leaves `running` once it has settled), queue and timer dropped. */
  const stop = (key: string) => {
    for (const j of running.current) if (j.key === key) j.ctl.abort();
    queue.current = queue.current.filter((k) => k !== key);
    const t = timers.current.get(key);
    if (t) window.clearTimeout(t);
    timers.current.delete(key);
  };

  /** Put `file` in slot `key` (replacing what was there): checked at once, then queued. */
  const pick = useCallback(
    (key: string, kind: K, file: File) => {
      const c = opts.current;
      stop(key);
      const old = slotsRef.current[key];
      if (old) URL.revokeObjectURL(old.previewUrl);
      const type = typeOf(file);
      const r = c.rule(kind);
      const cap = isImage(type) ? Math.max(c.maxPickBytes, r.maxBytes) : r.maxBytes;
      const error = !c.types.includes(type)
        ? c.messages.badType
        : !r.formats.includes(isImage(type) ? 'image' : 'pdf')
          ? isImage(type)
            ? c.messages.pdfOnly
            : c.messages.photoOnly
          : file.size > cap
            ? c.messages.tooLarge(c.formatBytes(cap))
            : null;
      const gen = (files.current.get(key)?.gen ?? 0) + 1;
      const base = { kind, name: file.name, size: file.size, type, previewUrl: URL.createObjectURL(file) };
      const slot: UploadSlot<K> = error ? { ...base, status: 'failed', error, retryable: false } : { ...base, status: 'preparing' };
      setSlots((s) => ({ ...s, [key]: slot }));
      files.current.set(key, { kind, file, attempts: 0, gen });
      if (!error) enqueue(key);
    },
    [enqueue],
  );

  const remove = useCallback((key: string) => {
    stop(key);
    files.current.delete(key);
    const old = slotsRef.current[key];
    if (old) URL.revokeObjectURL(old.previewUrl);
    setSlots((s) => {
      const next = { ...s };
      delete next[key];
      return next;
    });
  }, []);

  /** A manual retry of a failed slot (its file is still in memory). */
  const retry = useCallback(
    (key: string) => {
      const entry = files.current.get(key);
      if (!entry) return;
      entry.attempts = 0;
      setState(key, { status: 'preparing' });
      enqueue(key);
    },
    [enqueue, setState],
  );

  /** Send every usable file again (the server's quarantine expired): a file refused for itself stays refused. */
  const resendAll = useCallback(() => {
    for (const [key, entry] of files.current) {
      const s = slotsRef.current[key];
      if (s?.status === 'failed' && !s.retryable) continue;
      stop(key);
      entry.attempts = 0;
      entry.gen++;
      setState(key, { status: 'preparing' });
      enqueue(key);
    }
  }, [enqueue, setState]);

  /**
   * A slot whose file was already uploaded in an earlier life of this page (a draft restored after a reload): done,
   * with its upload id, no file and no preview.
   */
  const adopt = useCallback((key: string, kind: K, uploadId: string, name: string) => {
    files.current.delete(key);
    setSlots((s) => ({ ...s, [key]: { kind, name, size: 0, type: '', previewUrl: '', status: 'done', uploadId } }));
  }, []);

  /** Release every file and preview (personal documents leave memory once they are no longer needed). */
  const clear = useCallback(() => {
    for (const key of files.current.keys()) stop(key);
    files.current.clear();
    Object.values(slotsRef.current).forEach((s) => s?.previewUrl && URL.revokeObjectURL(s.previewUrl));
    setSlots({});
  }, []);

  // the connection is back: everything waiting for it goes again
  useEffect(() => {
    const back = () => {
      for (const [key, s] of Object.entries(slotsRef.current)) if (s?.status === 'waiting') enqueue(key);
    };
    window.addEventListener('online', back);
    return () => window.removeEventListener('online', back);
  }, [enqueue]);

  // leaving: stop every upload and timer, release the previews
  useEffect(() => {
    alive.current = true;
    const r = running.current;
    const t = timers.current;
    return () => {
      alive.current = false;
      queue.current = [];
      r.forEach((j) => j.ctl.abort());
      t.forEach((id) => window.clearTimeout(id));
      Object.values(slotsRef.current).forEach((s) => s && URL.revokeObjectURL(s.previewUrl));
    };
  }, []);

  return { slots, pick, remove, retry, resendAll, clear, adopt };
}

export type UploadQueue<K extends string> = ReturnType<typeof useUploadQueue<K>>;

/** The server's id of a finished upload, or null. */
export const uploadIdOf = (s: UploadState | undefined) => (s?.status === 'done' ? s.uploadId : null);
/** Why it failed, or null. */
export const uploadErrorOf = (s: UploadState | undefined) => (s?.status === 'failed' ? s.error : null);
/** Still on its way (preparing, sending, waiting for the connection or a retry). */
export const uploadBusy = (s: UploadState | undefined) => !!s && s.status !== 'done' && s.status !== 'failed';
